import { motion } from 'framer-motion';
import { Activity, ChevronLeft, ChevronRight } from 'lucide-react';
import type { ReactElement } from 'react';
import { NavLink } from 'react-router-dom';

import { NAVIGATION } from '@/app/router/navigation';
import { useAppDispatch, useAppSelector } from '@/app/store/hooks';
import {
  selectSidebarCollapsed,
  sidebarToggled,
} from '@/features/ui';
import { cn } from '@/shared/lib/cn';
import { Tooltip, TooltipProvider } from '@/shared/ui/Tooltip';

export function Sidebar(): ReactElement {
  const collapsed = useAppSelector(selectSidebarCollapsed);
  const dispatch = useAppDispatch();

  return (
    <TooltipProvider delayDuration={150}>
      <motion.aside
        initial={false}
        animate={{ width: collapsed ? 76 : 264 }}
        transition={{ duration: 0.25, ease: [0.16, 1, 0.3, 1] }}
        className={cn(
          'sticky top-0 z-30 hidden h-screen flex-col border-r border-sidebar-border',
          'bg-sidebar text-sidebar-foreground md:flex',
        )}
      >
        {/* Brand */}
        <div className="flex h-16 items-center gap-3 px-5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-gradient-to-br from-primary to-accent text-primary-foreground shadow-soft-2">
            <Activity className="h-4.5 w-4.5" />
          </span>
          {!collapsed ? (
            <div className="overflow-hidden">
              <div className="font-display text-base font-semibold leading-none tracking-tight">
                SISMA
              </div>
              <div className="mt-1 text-[10px] uppercase tracking-widest text-sidebar-foreground/55">
                Salud Mental
              </div>
            </div>
          ) : null}
        </div>

        <div className="mx-3 h-px bg-sidebar-border" />

        {/* Sections */}
        <nav className="flex-1 overflow-y-auto px-3 py-4">
          <ul className="space-y-6">
            {NAVIGATION.map((section) => (
              <li key={section.label}>
                {!collapsed ? (
                  <div className="px-2 pb-2 text-[10px] font-semibold uppercase tracking-widest text-sidebar-foreground/45">
                    {section.label}
                  </div>
                ) : (
                  <div className="mx-2 mb-2 h-px bg-sidebar-border/60" />
                )}
                <ul className="space-y-1">
                  {section.items.map((item) => (
                    <li key={item.to}>
                      <SidebarItem
                        to={item.to}
                        label={item.label}
                        icon={<item.icon className="h-4 w-4" />}
                        collapsed={collapsed}
                        disabled={item.disabled === true}
                      />
                    </li>
                  ))}
                </ul>
              </li>
            ))}
          </ul>
        </nav>

        {/* Collapse toggle */}
        <div className="border-t border-sidebar-border p-3">
          <button
            type="button"
            onClick={() => {
              dispatch(sidebarToggled());
            }}
            aria-label={collapsed ? 'Expandir menú' : 'Colapsar menú'}
            className={cn(
              'flex w-full items-center gap-2 rounded-lg px-3 py-2 text-xs font-medium',
              'text-sidebar-foreground/70 transition-colors',
              'hover:bg-sidebar-accent hover:text-sidebar-accent-foreground',
            )}
          >
            {collapsed ? (
              <ChevronRight className="h-4 w-4" />
            ) : (
              <>
                <ChevronLeft className="h-4 w-4" />
                <span>Colapsar</span>
              </>
            )}
          </button>
        </div>
      </motion.aside>
    </TooltipProvider>
  );
}

interface SidebarItemProps {
  readonly to: string;
  readonly label: string;
  readonly icon: ReactElement;
  readonly collapsed: boolean;
  readonly disabled: boolean;
}

function SidebarItem({
  to,
  label,
  icon,
  collapsed,
  disabled,
}: SidebarItemProps): ReactElement {
  const content = (
    <NavLink
      to={to}
      end={to === '/'}
      aria-disabled={disabled}
      onClick={(e) => {
        if (disabled) e.preventDefault();
      }}
      className={({ isActive }) =>
        cn(
          'group relative flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium',
          'transition-colors duration-150 ease-out-expo',
          collapsed ? 'justify-center' : '',
          disabled
            ? 'cursor-not-allowed text-sidebar-foreground/35'
            : isActive
              ? 'bg-sidebar-accent text-sidebar-accent-foreground shadow-soft-1'
              : 'text-sidebar-foreground/85 hover:bg-sidebar-accent/60 hover:text-sidebar-accent-foreground',
        )
      }
    >
      {({ isActive }) => (
        <>
          {isActive && !disabled ? (
            <span className="absolute inset-y-1 left-0 w-0.5 rounded-r-full bg-primary" aria-hidden />
          ) : null}
          <span className="grid place-items-center">{icon}</span>
          {!collapsed ? <span className="truncate">{label}</span> : null}
          {disabled && !collapsed ? (
            <span className="ml-auto rounded-full bg-sidebar-border px-1.5 py-0.5 text-[9px] uppercase tracking-wider">
              próximo
            </span>
          ) : null}
        </>
      )}
    </NavLink>
  );

  if (collapsed) {
    return (
      <Tooltip content={label} side="right">
        {content}
      </Tooltip>
    );
  }
  return content;
}
